import { BcmOrderStatusType } from '../models/bcm-order-status';
import { isOrderStatusLockedForEditing } from './order-status-model.util';

/**
 * Abrechnungslogik Warenkorb/Bierdeckel laut AV-003 (#6664).
 * Referenz: docs/auftragsverwaltung/AV-003-abrechnungslogik-bierdeckel.md
 */

export enum OrderBillingContainerLevel {
    OrderCart = 'order_cart',
    PersonSlipVoucher = 'person_slip_voucher',
    CompanySlipVoucher = 'company_slip_voucher',
    BoatAssignment = 'boat_assignment',
}

export enum OrderBillingSettlementPath {
    SlipVoucher = 'slip_voucher',
    TseDirect = 'tse_direct',
    CompleteWithoutSettlement = 'complete_without_settlement',
}

export enum InvoicePositionSettlementState {
    Open = 'open',
    Invoiced = 'invoiced',
    ReceiptPaid = 'receipt_paid',
    SoftDeleted = 'soft_deleted',
}

export interface SlipVoucherBillingRecipient {
    personId: number | null;
    companyId: number | null;
    boatId: number | null;
}

export interface InvoicePositionBillingSnapshot {
    bcm_invoices_id?: number | null;
    bcm_receipts_id?: number | null;
    deletedOn?: string | Date | null;
    bcm_persons_id?: number | null;
    bcm_companies_id?: number | null;
    bcm_boats_id?: number | null;
    priceLock?: boolean;
    titleLock?: boolean;
    accountLock?: boolean;
    itemNumberLock?: boolean;
    unitLock?: boolean;
    taxRateLock?: boolean;
}

export interface OrderBillingTransferInput {
    orderStatus: BcmOrderStatusType | string;
    settlementPath: OrderBillingSettlementPath;
    recipient: SlipVoucherBillingRecipient | null;
    transferPositionIds: number[];
    allCartPositionIds: number[];
    unconvertedBillableTimeEntryCount?: number;
}

export interface OrderBillingTransferResult {
    ok: boolean;
    errorKey?: string;
    isPartialTransfer?: boolean;
    targetOrderStatus?: BcmOrderStatusType;
}

export const BILLING_REVISION_AUDIT_FIELDS = [
    'lastUpdateOn',
    'lastUpdateBy',
] as const;

export const BILLING_REVISION_ORDER_LINK_TABLE = 'bcm_order_invoice_positions';

export function resolveInvoicePositionSettlementState(
    snapshot: InvoicePositionBillingSnapshot,
): InvoicePositionSettlementState {
    if (snapshot.deletedOn) {
        return InvoicePositionSettlementState.SoftDeleted;
    }

    if (snapshot.bcm_invoices_id) {
        return InvoicePositionSettlementState.Invoiced;
    }

    if (snapshot.bcm_receipts_id) {
        return InvoicePositionSettlementState.ReceiptPaid;
    }

    return InvoicePositionSettlementState.Open;
}

export function isOpenSlipVoucherPosition(snapshot: InvoicePositionBillingSnapshot): boolean {
    return resolveInvoicePositionSettlementState(snapshot) === InvoicePositionSettlementState.Open
        && (!!snapshot.bcm_persons_id || !!snapshot.bcm_companies_id);
}

export function isPositionOnOrderCart(
    snapshot: InvoicePositionBillingSnapshot,
    linkedToOrder: boolean,
): boolean {
    if (!linkedToOrder) {
        return false;
    }

    const state = resolveInvoicePositionSettlementState(snapshot);

    if (state !== InvoicePositionSettlementState.Open) {
        return false;
    }

    return !snapshot.bcm_persons_id && !snapshot.bcm_companies_id;
}

export function resolveBillingContainerLevel(
    snapshot: InvoicePositionBillingSnapshot,
    linkedToOrder = false,
): OrderBillingContainerLevel {
    if (isPositionOnOrderCart(snapshot, linkedToOrder)) {
        return OrderBillingContainerLevel.OrderCart;
    }

    if (snapshot.bcm_persons_id) {
        return snapshot.bcm_boats_id
            ? OrderBillingContainerLevel.BoatAssignment
            : OrderBillingContainerLevel.PersonSlipVoucher;
    }

    if (snapshot.bcm_companies_id) {
        return snapshot.bcm_boats_id
            ? OrderBillingContainerLevel.BoatAssignment
            : OrderBillingContainerLevel.CompanySlipVoucher;
    }

    return OrderBillingContainerLevel.OrderCart;
}

export function resolveSlipVoucherRecipientFromOrderClient(
    clientPersonId?: number | null,
    clientCompanyId?: number | null,
    boatId?: number | null,
): { ok: true; recipient: SlipVoucherBillingRecipient } | { ok: false; errorKey: string } {
    const personId = clientPersonId ?? null;
    const companyId = clientCompanyId ?? null;

    if (!personId && !companyId) {
        return { ok: false, errorKey: 'bcmErrors.orders.billingNoClient' };
    }

    if (personId && companyId) {
        return { ok: false, errorKey: 'bcmErrors.orders.billingInvalidTarget' };
    }

    return {
        ok: true,
        recipient: {
            personId,
            companyId,
            boatId: boatId ?? null,
        },
    };
}

export function validatePartialTransferSelection(
    transferPositionIds: number[],
    allCartPositionIds: number[],
): { ok: boolean; errorKey?: string; isPartialTransfer: boolean } {
    if (!transferPositionIds.length) {
        return {
            ok: false,
            errorKey: 'bcmErrors.orders.billingEmptyCart',
            isPartialTransfer: false,
        };
    }

    const cartSet = new Set(allCartPositionIds);
    const invalidId = transferPositionIds.find((id) => !cartSet.has(id));

    if (invalidId != null) {
        return {
            ok: false,
            errorKey: 'bcmErrors.orders.billingInvalidTransferSelection',
            isPartialTransfer: false,
        };
    }

    const isPartialTransfer = transferPositionIds.length < allCartPositionIds.length;

    return { ok: true, isPartialTransfer };
}

export function resolveTargetOrderStatusAfterTransfer(
    settlementPath: OrderBillingSettlementPath,
    isPartialTransfer: boolean,
): BcmOrderStatusType | null {
    if (isPartialTransfer) {
        return null;
    }

    switch (settlementPath) {
        case OrderBillingSettlementPath.TseDirect:
            return BcmOrderStatusType.Payed;

        case OrderBillingSettlementPath.SlipVoucher:
        case OrderBillingSettlementPath.CompleteWithoutSettlement:
            return BcmOrderStatusType.Done;

        default:
            return null;
    }
}

export function validateOrderCartBillingTransfer(
    input: OrderBillingTransferInput,
): OrderBillingTransferResult {
    const {
        orderStatus,
        settlementPath,
        recipient,
        transferPositionIds,
        allCartPositionIds,
        unconvertedBillableTimeEntryCount = 0,
    } = input;

    if (isOrderStatusLockedForEditing(orderStatus as BcmOrderStatusType)) {
        return { ok: false, errorKey: 'bcmErrors.orders.lockedForEditing' };
    }

    if (settlementPath === OrderBillingSettlementPath.SlipVoucher) {
        if (!recipient || (!recipient.personId && !recipient.companyId)) {
            return { ok: false, errorKey: 'bcmErrors.orders.billingNoClient' };
        }

        if (recipient.personId && recipient.companyId) {
            return { ok: false, errorKey: 'bcmErrors.orders.billingInvalidTarget' };
        }
    }

    const selectionCheck = validatePartialTransferSelection(transferPositionIds, allCartPositionIds);

    if (!selectionCheck.ok) {
        return {
            ok: false,
            errorKey: selectionCheck.errorKey,
        };
    }

    const isPartialTransfer = selectionCheck.isPartialTransfer
        || unconvertedBillableTimeEntryCount > 0;

    if (
        settlementPath === OrderBillingSettlementPath.CompleteWithoutSettlement
        && allCartPositionIds.length > 0
        && !isPartialTransfer
    ) {
        return { ok: false, errorKey: 'bcmErrors.orders.billingUnsettledCart' };
    }

    return {
        ok: true,
        isPartialTransfer,
        targetOrderStatus: resolveTargetOrderStatusAfterTransfer(
            settlementPath,
            isPartialTransfer,
        ) ?? undefined,
    };
}

export function canModifyOpenSlipVoucherPosition(
    snapshot: InvoicePositionBillingSnapshot,
    options: {
        linkedToOrder: boolean;
        orderLocked: boolean;
        modifyCartFields?: boolean;
    },
): { ok: boolean; errorKey?: string } {
    const state = resolveInvoicePositionSettlementState(snapshot);

    if (state === InvoicePositionSettlementState.SoftDeleted) {
        return { ok: false, errorKey: 'bcmErrors.orders.billingPositionDeleted' };
    }

    if (state === InvoicePositionSettlementState.Invoiced
        || state === InvoicePositionSettlementState.ReceiptPaid) {
        return { ok: false, errorKey: 'bcmErrors.orders.billingPositionAlreadySettled' };
    }

    const onCart = isPositionOnOrderCart(snapshot, options.linkedToOrder);

    if (onCart && options.orderLocked) {
        return { ok: false, errorKey: 'bcmErrors.orders.lockedForEditing' };
    }

    if (!onCart && options.modifyCartFields === true) {
        return { ok: false, errorKey: 'bcmErrors.orders.billingNotOnOrderCart' };
    }

    return { ok: true };
}

export function hasManualFieldLock(snapshot: InvoicePositionBillingSnapshot): boolean {
    return !!snapshot.priceLock
        || !!snapshot.titleLock
        || !!snapshot.accountLock
        || !!snapshot.itemNumberLock
        || !!snapshot.unitLock
        || !!snapshot.taxRateLock;
}

export function getBillingTransferRevisionMetadata(
    orderId: number,
    positionIds: number[],
    recipient: SlipVoucherBillingRecipient,
    actorSub: string,
): Record<string, unknown> {
    return {
        orderId,
        positionIds,
        recipientPersonId: recipient.personId,
        recipientCompanyId: recipient.companyId,
        recipientBoatId: recipient.boatId,
        actorSub,
        orderLinkTable: BILLING_REVISION_ORDER_LINK_TABLE,
        auditFields: [...BILLING_REVISION_AUDIT_FIELDS],
    };
}
